import { CreativeServerGenConfig } from "../Creative/CreativeServer";
import { DynamicFormElement } from "../DynamicFormElement";
import { FanboxColorName } from "../Utility/Color";
import { CreateVoting } from "./ConfigRender";
import { ContextTrigger } from "./ContentContext";
import { convertPartyIdToHex, PartyComponentId } from "./PartyId";


export type PartyConfigData = {
  itemId: string;
  title: string;
  image: string;
  description: string;
  main: ActivityBlockConfigData[];
  optional: {[key : string] : ActivityBlockConfigData}
  media: Media[];
  next?: string;
  chats : {[key : string] : ChatConfigData},
  triggers : ContextTrigger[];
};

export type Media = {
  mediaType: string;
  url: string;
  length : number;
  startTime : number;
};

export const ExpressionColors = ["#4040FF", "#FF0000", "#D4D435", "#60BF60", "#D435D4", "#35D4D4"];


export type DisplayPanel = "left" | "right";
export type ElementRotate = "pyramid" | "sequential" | "random" | "carousel" | "none";
export type PostLayout = "single-element" | "double-element";
export type PostPanelLayout = "primary-left" | "primary-right" | "primary-only";
export type MessageDisplay = "post" | "element" | "none";
export type ActivityBlockType = 'post' | 'quiz' | 'creative' | "chat" | 'submission';
//export type ChatSource = 'integrated' | 'global' | 'none';
export type ChatType = 'read-only' | 'like-only' | 'comment' | 'reply' | 'threaded';
export type ActivityPosition = 'top' | 'middle' | 'bottom' | 'all' | 'none';
//export type PostChatShareMode = 'shared' | 'alternate';

export type ChatConfigData = {
  itemId : string;
  chatType : ChatType;
  messages : MessageConfigData[];
  threads : ChatConfigData[];
  startTime : number;
  endTime : number;
}



export type ActivityBlockConfigData = {
  startTime : number;
  endTime : number;
  activityType : ActivityBlockType;
  //chatSource : ChatSource;
  chatSourceId? : string;
  mediaPosition : ActivityPosition;
  //chatPosition? : ActivityPosition;
 // postChatShareMode? : PostChatShareMode;
  config : PostConfigData | QuizConfigData | CreativeConfigData | CreativeServerGenConfig | ChatConfigData | SubmissionCompetitionConfigData;
  forceOptionalEnd : boolean;
}

export const DefaultActivityBlockConfigData = () : ActivityBlockConfigData => {
  return {
    startTime : 0,
    endTime : 0,
    activityType : 'post',
    //chatSource : 'none',
    mediaPosition : 'none',
    config : DefaultPostConfigData(),
    forceOptionalEnd : false
  }
}

export type PostConfigData = {
  itemId: string;
  title: string;
  author: string;
  userColor? : string;
  elements: PostElementConfigData[];
  layout: PostLayout;
  rotate: ElementRotate;
  chatId? : string;
  messageDisplay: MessageDisplay;
  messageMixRatio?: number;
  voting?: VotesConfigData;
  elementDisplayTime? :  number;
  elementIndex? : number;
};

export const DefaultPostConfigData = () : PostConfigData =>{
  return {
    itemId :'',
    title : '',
    author : '',
    elements : [],
    layout : 'single-element',
    rotate : 'none',
    messageDisplay : 'none',
  }
}

export type PostElementConfigData = {
  itemId: string;
  title?: string;
  primary: PostPanelConfigData;
  secondary?: PostPanelConfigData;
  layout: PostPanelLayout;
  chatId? : string;
  voting?: VotesConfigData;
};

export type PostPanelConfigData = {
  text?: string;
  imageSrc?: string;
  imageClick?: string;
  buttonText?: string;
  buttonClick?: string;
  buttonColor? : FanboxColorName;
  buttonTextColor? : FanboxColorName;
}

export type MessageConfigData = {
  itemId: string;
  userId: string;
  nickname: string;
  userIcon: string;
  userColor: string;
  title? : string;
  bodyText: string;
  voting: VotesConfigData;
  replies : MessageConfigData[];
  image?: string;
  displayStart? : number;
  displayEnd? : number;
};

export const EmptyMessageConfigData = () : MessageConfigData => {
  return {
    itemId : '',
    userIcon : '',
    userColor : '',
    userId : '',
    nickname : '',
    bodyText : '',
    voting : EmpyVotesConfig(),
    replies : [],
  }
}

export const MessageConfigFromPostConfig = (post : PostConfigData,  multiplier : number, boosts : number, online : number, elementIndex : number = 0, userId? : string) : [MessageConfigData, string] =>{
  const postElement = post.elements[elementIndex];
  const primaryText = postElement.primary.text;
  const secondaryPanel = postElement.secondary;
  const secondaryText = (secondaryPanel !== undefined ? secondaryPanel.text : undefined) ?? '';
  const bodyText =  primaryText ?? secondaryText;
  const image = postElement.primary.imageSrc !== undefined ? postElement.primary.imageSrc : postElement.secondary?.imageSrc;

  const partyId = (()=>{
    const n = parseInt(post.itemId.slice(0,2));
    return isNaN(n) ? 0 : n;
  })();

  const chatId : PartyComponentId = {
    id : partyId,
    chat : { id : post.itemId }
  }

  const chatHexId = convertPartyIdToHex(chatId);

  const messageId : PartyComponentId = {
    id : partyId,
    chat : { id : post.itemId , message : {id : 0}}
  }

  const messageHexId = convertPartyIdToHex(messageId);

  return [{
    itemId : messageHexId.id,
    title : post.title,
    nickname : post.author,
    userId : userId ?? 'unknown',
    userIcon : '',
    userColor : post.userColor ?? (()=> { const index = Math.floor(Math.random() * ExpressionColors.length); return ExpressionColors[index];})(),
    bodyText : bodyText,
    image : image, 
    replies : [],
    voting : CreateVoting(messageId, 'inspiring', Date.now(), multiplier, 5000, 15000, boosts, online )
  }, chatHexId.id]

}

export type ExpressionType =
  | "undefined"
  | "inspiring"
  | "appreciative"
  | "smart"
  | "perceptive"
  | "informative"
  | "funny";


export const ConvertExpressionTypeToNumber = (expression: ExpressionType) => {
  if (expression === 'undefined')
    return -1;
  else if (expression === "inspiring")
    return 0;
  else if (expression === "appreciative")
    return 1;
  else if (expression === "smart")
    return 2;
  else if (expression === "perceptive")
    return 3;
  else if (expression === "informative")
    return 4;
  else
    return 5;
}



export const ConvertNumberToExpressionType = (n: number): ExpressionType => {
  if ((n < 0) || (n > 5))
    return 'undefined';
  else if (n === 0)
    return 'inspiring';
  else if (n === 1)
    return 'appreciative';
  else if (n === 2)
    return 'smart';
  else if (n === 3)
    return 'perceptive';
  else if (n === 4)
    return 'informative';
  else
    return 'funny';
}

export type VotesConfigData = {
  itemId: string,
  impressions: number;
  likes: number;
  comments: number;
  expression?: ExpressionType;
  created: number;
  updated: number;
  popularity? : number;
  affect? : number;
  react? : number;
  userLiked? : boolean;
};

export const EmpyVotesConfig = () : VotesConfigData => {
  return {
    itemId : '',
    impressions : 0,
    likes : 0,
    comments : 0,
    created : 0,
    updated : 0
  }
}

const _e15 = Math.pow(Math.E, 1.5);
const _vpop_alpha = 0.000961856; //1.89485E-05;
const _vpop_delta = 3.13939468;

export const CalculatePopularityForVotes = (votes : VotesConfigData) : number => {
  const time = votes.updated - votes.created;
  const baseline = (Math.log(_e15 + _vpop_alpha * time) - 1.5)/_vpop_delta;
  votes.popularity = votes.impressions / baseline;
  return votes.popularity;
}

export const GeneratePopularity = (curated : boolean, multiplier? : number, boost : number = 0, online : number = 0) => {
  const alpha = curated ? 0.25 : 0.6;
  const range = curated ? 0.83 : 0.9;
  const min = curated ? 0.05 : 0.01;
  const max = curated ? 1.0 : 0.9;
  const scale = (curated ? 11973 : 464) / (multiplier ?? 1.0);
  const r = Math.random() * range;
  const raw = 10 / Math.pow(1-r, 1/ alpha);
  const pop =  (raw / scale) * (max - min) + min;
  if(pop > 1.0 || pop < min) 
  {
    console.log('popularity= ' + pop + ' alpha = ' + alpha + ' max = ' + range + ' scale = ' + scale + ' r = ' + r + 'max = ' + raw + ' pop = ' + pop);
  }
  if(online > 1)
  {
    const pop2 = pop * online + boost;
    return Math.min(pop2,online * 2.0);
  }
  else
    return pop;
}


export const GenerateAffectReactPair = () : [number, number] => {
  const r1 = 10.4 + 2.6 * Math.random();
    const r2 = (6.16 + 1.54 * Math.random()) * r1;
    return [1/r1, 1/r2];
}

export const ProjectVoting = (votes : VotesConfigData, newTime : number) => {
  const time = newTime - votes.created;
  
  const popularity = votes.popularity ?? CalculatePopularityForVotes(votes);
  const projection = popularity * (Math.log(_e15 + _vpop_alpha * time) - 1.5)/_vpop_delta;
  const [affect, react] = (votes.affect && votes.react) ? [votes.affect, votes.react] : (()=>{ const [a,r] = GenerateAffectReactPair(); votes.affect = a; votes.react = r; return [a,r]})();
  votes.impressions = Math.round(projection);
  votes.likes = Math.round(projection * affect);
  votes.comments = Math.round(projection * react);
  votes.updated = newTime;
}

export const InitialiseImpressions = (votes : VotesConfigData, delta : number, online : number) : [number,number] => {
  const popularity = votes.popularity ?? 0.1 * online * 2.0;
  const projection = popularity * (Math.log(_e15 + _vpop_alpha * delta) - 1.5)/_vpop_delta;
  return [popularity,projection];
}

export type QuizBankUsage = "sequential" | "interleaved-ordered" | "interleaved-random";
export type QuizType = 'quiz' | 'survey' | 'profile';

export type QuizTreeAlgorithm = "map" | "AgtB" | "threshA" | "threshAorB" | "threshAandB" | "threshAnotB" | "threshAxorB" | "threshAxB";
export type QuizTimerMode = "none" | "question" | "total";
export type QuizImageAspectRatio = "21:9" | "16:9" | "3:2" | "4:3" | "1:1" | "3:4" | "2:3" | "9:16" | "9:21";
export type QuizAnswerType = "select" | "matchAnswer" | "slide" | "counterSlide";

export type QuizConfigData = {
  itemId: string;
  quizType : QuizType;
  title: string;
  subTitle : string;
  description: string;
  image : string;
  timerMode: QuizTimerMode;
  timeBonusMultiplier? : number;
  questionTimer?: number;
  scrambleQuestions: boolean;
  scrambleAnswers: boolean;
  pointsPerQuestion: number;
  bankUsage: QuizBankUsage;
  banks: QuizBankConfig[];
  metrics: string[];
  scores? : QuizScores;
  survey? : QuizSurveyResults;
  defaultProfile? : QuizProfile;
}

export const CloneForQuizClient = (quiz : QuizConfigData) : QuizConfigData => {
  return {
    itemId : quiz.itemId,
    quizType : quiz.quizType,
    title : quiz.title,
    subTitle : quiz.subTitle,
    description : quiz.description,
    image : quiz.image,
    timerMode : quiz.timerMode,
    timeBonusMultiplier : quiz.timeBonusMultiplier,
    questionTimer : quiz.questionTimer,
    scrambleQuestions : quiz.scrambleQuestions,
    scrambleAnswers : quiz.scrambleAnswers,
    pointsPerQuestion : quiz.pointsPerQuestion,
    bankUsage : quiz.bankUsage,
    banks : quiz.banks,
    metrics : quiz.metrics
  }
}

export type QuizProfile = {
  title : string;
  categorization : string;
  catText : string;
  catImage? : string;
  components : { heading : string, text : string, image? : string};
}

export type QuizScoreRanking = { nickname : string, score : number};

export type QuizScores =  {
  rankings : QuizScoreRanking[];
  averageCorrect : number;
  averageQuestionScore : number;
  averageTime : number;
  averageTimeBonus : number;
  uniquePlayers : number;
  shareImage : string[];
  shareTexts : string[];
  shareTitles : string[];

}

export type QuizSurveyAnswerResult = {
  name : string;
  votes : number;
}

export type QuizSurveyQuestionResult = {
  results :  QuizSurveyAnswerResult[],
  question : string;
  annotation : string;

}
export type QuizSurveyResults = {
  questions : QuizSurveyQuestionResult[]
}

export type QuizResults = {
  results: { [key: string]: QuizResult[] };
  timeBonus : number;
  answerPoints : number;
}

export const EmptyQuizResults = () : QuizResults => {
  return {
    results : {},
    timeBonus : 0,
    answerPoints : 0
  };
}

export type QuizResult = {
  questionId: string;
  answers: QuizAnswerConfig[];
  selected: number[];
  correct: boolean;
  points : number;
  values: number[];
  metrics: { name: string, value: number }[];
  timeTaken: number;


}

export const CalculateQuizScorePosition = (results : QuizResults, field : QuizScores) : { absolute : number, relative : number} => {
  const score = results.answerPoints + results.timeBonus;
  var range = 2 * (field.rankings[0].score - field.averageQuestionScore - field.averageTimeBonus);
  range = 0.5 * Math.min(field.rankings[0].score, range);

  var relativeScore = (score - field.averageQuestionScore - field.averageTimeBonus) / range;
  relativeScore = relativeScore > 1.0 ? 1.0 : (relativeScore < -1.0  ? -1.0 : relativeScore);

  const relativePosition = 1.0 -  Math.acos(relativeScore) / Math.PI;
  var absolutePosition = Math.round(relativePosition * field.uniquePlayers);
  return { absolute : absolutePosition , relative : relativePosition};
}


export type QuizBankConfig = {
  itemId: string;
  questionTimer?: number;
  scrambleQuestions?: boolean;
  scrambleAnswers?: boolean;
  pointsPerQuestion?: number;
  pickQuestions?: number;
  simpleQuestions: QuizQuestionConfig[];
  adaptiveQuestions: QuizTreeConfig[];

};

export type QuizTreeConfig = {
  itemId: string;
  metricA?: string;
  metricB?: string;
  threshA?: number;
  threshB?: number;
  algo: QuizTreeAlgorithm;
  question: QuizQuestionConfig;
  children?: QuizTreeConfig[];
};

export type QuizQuestionConfig = {
  itemId: string;
  text?: string;
  images?: string[];
  answers: QuizAnswerConfig[];
  scramble?: boolean;
  addAnswersToImages?: string[];
  addAnswers?: string[];
  pickAnswers?: number;
  points?: number;
  questionTimer?: number;
  metricName?: string;
  metricPoints?: number;
  generator?: string[];
  generateRounds?: number;
  questionImageAspect?: QuizImageAspectRatio;
  answerImageAspect?: QuizImageAspectRatio;
  answerType?: QuizAnswerType;
  refill?: boolean;
  refillCount?: number;

}
export type QuizAnswerConfig = {
  correct?: boolean;
  text?: string;
  image?: string;
  matchId?: string;
  itemId: string;
}

export type CreativeQuestionOption = {
  text: string; // What the user sees
  image?: string[]; // Array of reference image URLs/paths, this is optional
}

export type CreativeQuestion = {
  text: string; // Question displayed to user
  options: CreativeQuestionOption[]; // Available choices
  selectCount : number// Whether multiple options can be selected
}

export type CreativeConfigData = {
  itemId : string;
  questions : CreativeQuestion[];
  image : string;
  title : string;
  subTitle : string;
  description : string;
  buttonText : string;
  thankYouText : string;
  
}

export type SubmissionCompetitionConfigData = {
  itemId : string;
  submission : DynamicFormElement[];
  title : string;
  subtitle : string; 
  description: string;
  thankYouMessage : string;
  image : string;
  buttonText : string;
  participantLimit : number;
  perParticipant : number;
}





export const getAllMediaURLs = (party: PartyConfigData): Array<string> => {
  const ret = new Array<string>();
  for (let j = 0; j < party.media.length; j++) {
    ret.push(party.media[j].url);
  }
  return ret;
}


